import { isNaN } from "lodash";
import { evaluate } from "mathjs";
import Decimal from "decimal.js-light";
import { formatPhoneNumber } from "react-phone-number-input";

import * as date from "@/utils/date";
import {
  PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
  PRICING_STRATEGY_CUSTOM_FORMULA_LABEL,
  PRICING_STRATEGY_LINEAL_METRES_LABEL,
  PRICING_STRATEGY_SQUARE_METRES_LABEL,
  PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL,
  PRODUCT_TYPE_ON_THE_FLY_PRODUCT_LABEL,
  PRODUCT_TYPE_PRODUCT_KIT_LABEL,
} from "@/utils/constants";
import { PRODUCT_TYPE_FLASHING_LABEL } from "../../utils/constants";

const getLineItem = (p) => {
  const baseItem = {
    product_type: p.productType,
    pricing_strategy: p.pricingStrategy ? p.pricingStrategy.replace(" ", "_").toLowerCase() : "",
    name: p.name || p.productName,
    description: p.productDescription,
    price: p.value ? `$${parseFloat(p.value)}` : "",
    total: p.totalValue ? `$${parseFloat(p.totalValue).toFixed(2)}` : "",
    quantity: p.quantity,
    types: [
      ...(p.types && Array.isArray(p.types)
        ? p.types
            .filter((type) => type.value !== "")
            .map((type) => ({
              ...type,
              label: type.name,
              value: type.value,
            }))
        : []),
    ],
  };

  if (
    [
      PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL,
      PRODUCT_TYPE_PRODUCT_KIT_LABEL,
      PRODUCT_TYPE_ON_THE_FLY_PRODUCT_LABEL,
    ].includes(p.productType)
  ) {
    if (p.pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
      return baseItem;
    } else if (p.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
      const lengths = p?.subitems ?? p?.lengthItems ?? [];
      const lengthTotal = p.sumLengths?.total ? parseFloat(p.sumLengths.total / 1000) : "";
      return {
        ...baseItem,
        quantity: lengthTotal,
        lengths: lengths.map((s) => ({
          amount: s.amount,
          length: s.length,
        })),
      };
    } else if (p.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
      const lengths = p?.subitems ?? p?.lengthItems ?? [];
      const sqmTotal = p.sumLengths?.total ? parseFloat(p.sumLengths?.total) : "";
      return {
        ...baseItem,
        quantity: sqmTotal,
        dimensions: lengths.map((s) => ({
          length: s.length,
          width: s.width,
        })),
      };
    } else if (p.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
      const formula = p.customFormula.elements.map((e) => e.value).join("");
      const output = evaluate(formula);
      const answer =
        isNaN(output) || output === Infinity
          ? ""
          : p.customFormula.isRoundedToNearestWholeNumber
          ? Math.round(output)
          : Decimal(output).toDecimalPlaces(4).toFixed(2);
      return {
        ...baseItem,
        custom_formula: p.customFormula.displayFormula.includes("displayOnInvoicePdf")
          ? p.customFormula.elements
              .filter((e) => e.type === "variable")
              .map((e) => ({
                label: e.name,
                value: e.value ? e.value.match(/\d+/g).join("") : "",
              }))
          : "",
        custom_formula_answer: p.customFormula.displayFormula.includes("displayOnInvoicePdf")
          ? answer
          : "",
      };
    }
  } else if (p.productType === "Notes" && p.notesType === 2) {
    return {
      ...baseItem,
      name: p.notes,
      description: p.productDescription,
      price: "",
      total: "",
      quantity: "Note",
    };
  } else if (p.productType === "Labour") {
    return {
      ...baseItem,
      pricing_strategy: "",
      name: p?.name ?? p.productName ?? "",
      description: "",
    };
  }
};

export const getDataTemplate = ({
  order,
  products,
  drawings,
  company,
  customer = null,
  templateType,
}) => {
  const baseData = {
    accounting_number: order?.accountingInvoiceNumber ? order?.accountingInvoiceNumber : "",
    accounting_name:
      company?.accountingName && company.accountingName !== "None"
        ? `${company?.accountingName} #:` ?? ""
        : "",
    tax_invoice_number: order?.taxInvoiceNumber ? `#${order?.taxInvoiceNumber}` : "",
    company_logo: company.image ? company.image : "",
    company_name: company?.companyName ? company?.companyName ?? "" : "",
    company_address: [
      company?.billingAddress1 ?? "",
      company?.billingAddress2 ?? "",
      company?.billingCity ?? "",
      company?.billingCountryCode ?? "",
      company?.billingPostcode ?? "",
    ]
      .filter((address) => address !== null && address !== "")
      .join(" "),
    company_email: company?.email ? company?.email ?? "" : "",
    company_phone: company?.phoneNumber ? formatPhoneNumber(company?.phoneNumber) ?? "" : "",
    company_abn: company?.abn ? company?.abn ?? "" : "",
    company_bsb: customer?.bsb ? customer?.bsb : company?.bsb,
    company_bank_account_number: customer?.bankAccountNumber
      ? customer?.bankAccountNumber
      : company?.bankAccountNumber,
    company_bank_account_name: customer?.bankAccountName
      ? customer?.bankAccountName
      : company?.bankAccountName,
    po: order?.reference ? order?.reference ?? "" : "",
    invoice_date: order?.invoiceDate ? order?.invoiceDate ?? "" : "",
    due_date: order?.invoiceDueDate ? order?.invoiceDueDate ?? "" : "",
    order_number: order?.purchaseOrder ? `#${order?.purchaseOrder}` ?? "" : "",
    customer_name: order?.customerName ? order?.customerName ?? "" : "",
    customer_address: [
      order?.billingAddress?.address1 ?? "",
      order?.billingAddress?.address2 ?? "",
      order?.billingAddress?.city ?? "",
      order?.billingAddress?.state ?? "",
      order?.billingAddress?.countryCode ?? "",
      order?.billingAddress?.postcode ?? "",
    ]
      .filter((address) => address !== null && address !== "")
      .join(" "),
    contact_name: order?.contact?.contactName ? order?.contact?.contactName ?? "" : "",
    contact_email: order?.contact?.email ? order?.contact?.email ?? "" : "",
    contact_phone_number: order?.contact?.phone
      ? formatPhoneNumber(order?.contact?.phone) ?? ""
      : "",
    contact_mobile_number: order?.contact?.mobile
      ? formatPhoneNumber(order?.contact?.mobile) ?? ""
      : "",
    // quote_number: "#00654377",
    // quote_date: "28/10/2024",
    custom_message: order.customMessage,
    created_date: order?.createdTimestamp
      ? date.toLocalDateTime({
          date: order.createdTimestamp,
          timezone: date.getCurrentUserTimezone(),
          format: "DD/MM/YYYY",
        })
      : "",
    required_date: order?.createdTimestamp
      ? date.toLocalDateTime({
          date: order.requiredTimestamp,
          timezone: date.getCurrentUserTimezone(),
          format: "DD/MM/YYYY",
        })
      : "",
    obtaining_method: order?.obtainingMethod ? order?.obtainingMethod ?? "" : "",
    pickup_notes: order?.pickupNotes ? order?.pickupNotes ?? "" : "",
    deliver_to: [
      order?.shippingAddress?.address1 ?? "",
      order?.shippingAddress?.address2 ?? "",
      order?.shippingAddress?.city ?? "",
      order?.shippingAddress?.state ?? "",
      order?.shippingAddress?.countryCode ?? "",
      order?.shippingAddress?.postcode ?? "",
    ]
      .filter((address) => address !== null && address !== "")
      .join(" "),
    install_at: [
      order?.installAddress?.address1 ?? "",
      order?.installAddress?.address2 ?? "",
      order?.installAddress?.city ?? "",
      order?.installAddress?.state ?? "",
      order?.installAddress?.countryCode ?? "",
      order?.installAddress?.postcode ?? "",
    ]
      .filter((address) => address !== null && address !== "")
      .join(" "),
    notes: order?.notes ? order?.notes ?? "" : "",
    delivery_fee:
      order.deliveryFee && parseFloat(order.deliveryFee) !== 0
        ? `$${parseFloat(order.deliveryFee).toFixed(2)}`
        : "",
    discounts: order.discounts,
    sub_total_value: order?.subTotal ? `$${parseFloat(order?.subTotal).toFixed(2) ?? 0.0}` : "",
    gst_value: order?.gst ? `$${parseFloat(order?.gst).toFixed(2) ?? 0.0}` : "",
    total_value: order?.totalPrice ? `$${parseFloat(order?.totalPrice).toFixed(2) ?? 0.0}` : "",
  };

  const productsData = {
    product_table: [
      ...drawings.map((d) => ({
        index: d.index,
        product_type: PRODUCT_TYPE_FLASHING_LABEL,
        name: d.name ? d.name : "",
        thickness: d.thickness ? d.thickness : "",
        girth: d.girth ? `${d.girth}mm` : "",
        bends: d.bends ? `${d.bends} bends` : "",
        lengths: d.subitems.map((s) => ({
          amount: s.amount,
          length: s.length,
        })),
        price: d.value ? `$${parseFloat(d.value)}` : "",
        total: d.totalValue ? `$${parseFloat(d.totalValue).toFixed(2)}` : "",
      })),
      ...products.map((p) => {
        if (p.productType === "Product Kit") {
          console.log(p);
          const baseKit = {
            name: p?.name ?? p.productName ?? "",
            description: p.productKitDescription,
            price: p.value ? `$${parseFloat(p.value)}` : "",
            total: p.totalValue ? `$${parseFloat(p.totalValue).toFixed(2)}` : "",
            product_type: p.productType,
            quantity: p.quantity ? parseFloat(p.quantity) : "",
            types:
              p.types && Array.isArray(p.types)
                ? p.types
                    .filter((t) => !t.isHideInvoicePdf)
                    .map((t) => ({
                      label: t.name,
                      value: t.value,
                    }))
                : [],
            kit_items: p.displayKitItems.includes("displayOnInvoicePdf")
              ? p.productKitItems.map((k) => {
                  const item = getLineItem(k);
                  return {
                    ...item,
                    types: [
                      ...(k.productRow?.material || k.productRow?.colour
                        ? [
                            {
                              label: k.productRow.material,
                              value: k.productRow.colour,
                            },
                          ]
                        : []),
                      ...item.types.filter((t) => !t.isHideInvoicePdf),
                    ],
                  };
                })
              : [],
            sub_kits: p.displaySubKits.includes("displayOnInvoicePdf")
              ? p.subKits.map((s) => ({
                  name: s.title,
                  description: "",
                  product_type: p.productType,
                  quantity: s.quantity ? parseFloat(s.quantity) : "",
                  price: s.value ? `$${parseFloat(s.value)}` : "",
                  total: s.totalValue ? `$${parseFloat(s.totalValue).toFixed(2)}` : "",
                  kit_items: s.kitProducts.map((k) => {
                    const item = getLineItem(k);
                    return {
                      ...item,
                      types: [
                        ...(k.productRow?.material || k.productRow?.colour
                          ? [
                              {
                                label: k.productRow.material,
                                value: k.productRow.colour,
                              },
                            ]
                          : []),
                        ...item.types.filter((t) => !t.isHideInvoicePdf),
                      ],
                    };
                  }),
                }))
              : [],
          };
          return baseKit;
        } else {
          const item = getLineItem(p);
          return {
            ...item,
            types: [
              // Looping the params is for getting the colour
              // in the non product kit items
              {
                label: p.materialName,
                value:
                  p?.params.find((pa) => pa.name === "Colour" && pa.value !== "")?.value ??
                  "" ??
                  "",
              },
              ...item.types.filter((t) => !t.isHideInvoicePdf),
            ],
          };
        }
      }),
    ],
  };

  const types = {
    INVOICE: {
      ...baseData,
      ...productsData,
    },
    PARTIAL_INVOICE: {
      ...baseData,
      ...productsData,
    },
  };

  return types[templateType];
};
